import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { FileFormatter } from '@repo/toolkit/file'
import { StringFormatter } from '@repo/toolkit/string'
import { FileTypeEnum, IFile } from '@repo/toolkit/types'
import ActionButton from '@repo/ui/action-button'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import UploadFileInput, { DisplayImage } from '@repo/ui/file-input'
import { BalloonIcon, DeleteIcon, UploadIcon } from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'

import { createCenterProgramDocument } from '../../../../api/centers/center-program-documents/create-center-program-document'
import { deleteCenterProgramDocument } from '../../../../api/centers/center-program-documents/delete-center-program-document'
import { getCenterProgramDocuments } from '../../../../api/centers/center-program-documents/get-center-program-documents'
import { getOlPermissions } from '../../../../lib/constants/ol-permissions'
import useAuth from '../../../../lib/hooks/use-auth'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../../lib/hooks/use-translate'
import { OLTypeEnum } from '../../../../lib/types/enums/ol/ol-type.enum'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import { CenterCostsTableMode } from './utils'

type Props = {
  centerId: number
  mode: CenterCostsTableMode
  olType?: OLTypeEnum
}

export function CenterProgramDocuments({ centerId, mode, olType }: Props) {
  const { t } = useTranslate()
  const { user } = useAuth()
  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'image-preview',
    IFile
  >(['image-preview'])
  const permissions = getOlPermissions({ role: user.role, type: olType })

  const { data: programDocuments, refetch } = useQuery({
    queryKey: ['center-program-documents', centerId],
    queryFn: () => getCenterProgramDocuments(centerId),
    initialData: []
  })
  const canEdit =
    (mode === 'action-edit' || mode === 'free-edit') &&
    permissions['center-program-documents-edit']

  const { mutateAsync: createProgramDocument } = useMutation({
    mutationFn: createCenterProgramDocument,
    onError: showAxiosError,
    onSuccess: async () => await refetch()
  })

  const { mutateAsync: deleteProgramDocument } = useMutation({
    mutationFn: deleteCenterProgramDocument,
    onError: showAxiosError,
    onSuccess: async () => await refetch()
  })

  async function handleCreateProgramDocument(files: IFile[]) {
    for (const file of files) await createProgramDocument({ centerId, ...file })
  }

  return (
    <Box style="border" className="bg-white-hover">
      <div className="flex flex-row items-center gap-2">
        <div className="w-8 h-8 flex items-center justify-center bg-secondary-light rounded p-1">
          <BalloonIcon color={tailwindColors.secondary.DEFAULT} />
        </div>

        <Typography variant="text">{t('center.costs.program.documents.title')}</Typography>
      </div>

      <div className="flex flex-col gap-2">
        {permissions['center-program-documents-missing-reminder'] &&
          programDocuments.length === 0 && (
            <Badge
              variant="danger"
              text="Don't forget to email the activity program to your contact in Paris at least a month prior arrival"
            />
          )}

        {canEdit && (
          <UploadFileInput
            placeholder={t('send.email.attachments.upload')}
            types={[FileTypeEnum.IMAGE, FileTypeEnum.PDF]}
            icon={UploadIcon}
            label={t('center.costs.program.documents.upload')}
            multiple
            onFilesChange={handleCreateProgramDocument}
          />
        )}

        {!canEdit && programDocuments.length === 0 && (
          <div className="flex flex-col items-center justify-center">
            <Typography variant="tag" color={tailwindColors.grey.light.active}>
              {t('center.costs.program.no.documents.uploaded')}
            </Typography>
          </div>
        )}

        <div className="flex flex-col gap-2">
          {programDocuments.map(({ id, ...document }, index) => (
            <div
              key={index}
              className={
                'flex bg-white w-full items-center justify-between p-2 border border-grey-light rounded-lg'
              }
            >
              <div className={'flex flex-1 items-center gap-2'}>
                <div className={'h-12 w-12 relative'}>
                  <div
                    className={'cursor-pointer absolute z-40 h-12 w-12'}
                    onClick={() => {
                      if (document.type === FileTypeEnum.XML) window.open(document.path, '_blank')
                      else toggleModal('image-preview', document)
                    }}
                  />

                  <DisplayImage
                    type={document.type}
                    src={document.path}
                    className={
                      'h-12 w-12 rounded bg-primary-light object-cover relative z-5 overflow-hidden'
                    }
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <Typography variant={'tag'} color={tailwindColors.grey.DEFAULT}>
                    {StringFormatter.shortenText(document.name, 30)}
                  </Typography>

                  <Typography variant={'label-medium'} color={tailwindColors.grey.light.active}>
                    {FileFormatter.size(document).value}
                  </Typography>
                </div>
              </div>

              {canEdit && (
                <ActionButton
                  variant={'danger'}
                  icon={DeleteIcon}
                  onClick={() => deleteProgramDocument({ id, centerId })}
                />
              )}
            </div>
          ))}
        </div>

        {selectedData && (
          <Modal
            isOpen={modalState['image-preview']}
            hideActions
            hideHeader
            size={'large'}
            onCancel={() => closeModel('image-preview')}
          >
            <DisplayImage
              allowAll
              type={selectedData.type}
              src={selectedData.path}
              className={'h-[70vh] w-full object-contain relative z-10 overflow-auto'}
            />
          </Modal>
        )}
      </div>
    </Box>
  )
}
